import React, { createContext, useContext, useState, useEffect } from 'react';
import { StaffUser, StaffRole } from '../types';
import { CaseDatabase } from '../services/db';

interface AuthContextType {
  isAuthenticated: boolean;
  user: StaffUser | null;
  login: (email: string, password?: string) => Promise<StaffUser | null>;
  logout: () => void;
  updatePassword: (newPassword: string, targetUser?: StaffUser) => Promise<void>;
  updateProfile: (name: string, email: string) => Promise<void>;
  verifyPassword: (password: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const BOOTSTRAP_KEY = 'lluis_bootstrap_v11_feb2026';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<StaffUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        const defaultAdmins = [
          { id: 'admin-001', name: 'Principal Attorney', email: 'admin@lluislaw.com', role: StaffRole.ADMIN },
          { id: 'admin-karl', name: 'Karl Lluis', email: 'karl@lluislaw.com', role: StaffRole.SUPER_ADMIN },
          { id: 'admin-david', name: 'David Lluis', email: 'david@lluislaw.com', role: StaffRole.SUPER_ADMIN }
        ];

        // Check if we need to re-bootstrap
        const currentBootstrap = localStorage.getItem('bootstrap_version');
        const needsBootstrap = currentBootstrap !== BOOTSTRAP_KEY;
        
        if (needsBootstrap) {
          console.log('Running bootstrap v10...');
          
          // Clear all existing staff to start fresh
          const allStaff = await CaseDatabase.getAllStaff();
          for (const staff of allStaff) {
            await CaseDatabase.deleteStaff(staff.id);
          }
          
          // Save the default admin users
          for (const adminInfo of defaultAdmins) {
            await CaseDatabase.saveStaff({
              id: adminInfo.id,
              name: adminInfo.name,
              email: adminInfo.email,
              role: adminInfo.role,
              password: 'admin',
              mustChangePassword: false,
              createdAt: new Date().toISOString()
            });
          }
          
          localStorage.setItem('bootstrap_version', BOOTSTRAP_KEY);
          console.log('Bootstrap complete');
        }

        const storedAuth = sessionStorage.getItem('lluis_auth');
        if (storedAuth) {
          const sessionUser = JSON.parse(storedAuth) as StaffUser;
          const dbUser = await CaseDatabase.getStaffByEmail(sessionUser.email);
          if (dbUser) {
            setIsAuthenticated(true);
            setUser(dbUser);
          } else {
            sessionStorage.removeItem('lluis_auth');
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setIsInitializing(false);
      }
    };

    init();
  }, []);

  const login = async (email: string, password?: string): Promise<StaffUser | null> => {
    const dbUser = await CaseDatabase.getStaffByEmail(email);
    console.log('Login attempt:', email, 'Found user:', dbUser ? 'yes' : 'no');
    
    if (dbUser && dbUser.password === password) {
      if (!dbUser.mustChangePassword) {
        setIsAuthenticated(true);
        setUser(dbUser);
        sessionStorage.setItem('lluis_auth', JSON.stringify(dbUser));
      }
      return dbUser;
    }
    
    return null;
  };

  const updatePassword = async (newPassword: string, targetUser?: StaffUser) => {
    const activeUser = targetUser || user;
    if (!activeUser) return;
    
    const updatedUser = { ...activeUser, password: newPassword, mustChangePassword: false };
    await CaseDatabase.saveStaff(updatedUser);
    
    if (!targetUser) {
      setUser(updatedUser);
      sessionStorage.setItem('lluis_auth', JSON.stringify(updatedUser));
    }
  };

  const updateProfile = async (name: string, email: string) => {
    if (!user) return;
    const updatedUser = { ...user, name, email };
    await CaseDatabase.saveStaff(updatedUser);
    setUser(updatedUser);
    sessionStorage.setItem('lluis_auth', JSON.stringify(updatedUser));
  };

  const verifyPassword = (password: string): boolean => {
    return user?.password === password;
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    sessionStorage.removeItem('lluis_auth');
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="fas fa-circle-notch animate-spin text-slate-900 text-4xl"></i>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Securing Environment...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, updatePassword, updateProfile, verifyPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
